import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrioSpinner } from '@prio365/prio365-react-library';
import { notification, Typography } from 'antd';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { DropTargetMonitor } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useSelector } from 'react-redux';
import {
  getProject,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import DroppableElement from '../../../../dnd/components/DroppableElement';
import {
  DND_TYPE_DRIVE_ITEM_FILE,
  DND_TYPE_EMAIL_ATTACHMENT,
  DndDriveItemDto,
} from '../../../../dnd/types';
import { DriveItem } from '../../../../models/Drive';
import { Project } from '../../../../models/Project';
import { ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { apiGetBlobOfDriveItem } from '../../../documents/api';
import { apiDownloadAttachmentToBase64 } from '../../../mail/api';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  dropZone: {
    border: `2px dashed ${theme.colors.application.border}`,
    borderRadius: theme.borderRadius.regular,
    padding: theme.spacing.large,
    textAlign: 'center',
    backgroundColor: theme.colors.application.background.light,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.small,
    '&:hover': {
      borderColor: theme.colors.base.blue[80],
      backgroundColor: theme.colors.base.blue[10],
    },
  },
  dropZoneSmall: {
    padding: theme.spacing.regular,
  },
  dropZoneActive: {
    borderColor: theme.colors.base.blue.default,
    backgroundColor: theme.colors.base.blue[10],
    borderStyle: 'solid',
  },
  dropZoneText: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.regular,
    display: 'inline-flex',
    gap: theme.spacing.regular,
    alignItems: 'center',
  },
  dropZoneSubtext: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.small,
  },
  uploadIcon: {
    fontSize: '1rem',
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.small,
  },
}));

interface FileDropZoneProps {
  projectId: ProjectId;
  isUploading?: boolean;
  onFileUpload: (file) => void;
  acceptedFileTypes?: string[];
  size?: 'small' | 'regular';
  title?: string;
  subtitle?: string;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  projectId,
  isUploading = false,
  onFileUpload,
  acceptedFileTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.tiff'],
  size = 'regular',
  title,
  subtitle,
}) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropLoading, setDropLoading] = useState(false);

  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );

  const handleFileUpload = async (file: File) => {
    const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.tiff'];
    const fileExtension =
      '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (!allowedTypes.includes(fileExtension)) {
      notification.error({
        message: t('accounting:incomingInvoices.upload.invalidFileType'),
        description: t(
          'accounting:incomingInvoices.upload.invalidFileTypeDescription',
          {
            types: allowedTypes.join(', '),
          }
        ),
      });
      return;
    }

    const maxSizeInMB = 10;
    if (file.size > maxSizeInMB * 1024 * 1024) {
      notification.error({
        message: t('accounting:incomingInvoices.upload.fileTooLarge'),
        description: t(
          'accounting:incomingInvoices.upload.fileTooLargeDescription',
          {
            maxSize: maxSizeInMB,
          }
        ),
      });
      return;
    }

    onFileUpload(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (isFileTypeAccepted(file.name)) {
        handleFileUpload(file);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const isFileTypeAccepted = (fileName: string): boolean => {
    const fileExtension = '.' + fileName.split('.').pop()?.toLowerCase();
    return acceptedFileTypes.includes(fileExtension);
  };

  // Handle file upload button click
  const handleUploadClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
      event.target.value = ''; // Reset input
    }
  };

  const disableDrag = (item: any, monitor: DropTargetMonitor) => {
    const itemType = monitor.getItemType();

    switch (itemType) {
      case DND_TYPE_DRIVE_ITEM_FILE: {
        const draggedElement = monitor.getItem() as DndDriveItemDto;
        // Only allow files
        return draggedElement.driveItems.some(
          (driveItem) => !!driveItem.folder
        );
      }
      case DND_TYPE_EMAIL_ATTACHMENT: {
        return false; // Allow email attachments
      }
      default:
        return true;
    }
  };

  const handleDndDrop = async (item: any, monitor: DropTargetMonitor) => {
    const itemType = monitor.getItemType();
    setDropLoading(true);

    switch (itemType) {
      case DND_TYPE_DRIVE_ITEM_FILE: {
        const draggedElement = item as DndDriveItemDto;
        const filesToDrop: File[] = [];
        for (const driveItem of draggedElement.driveItems) {
          if (!driveItem.folder) {
            const blob = await apiGetBlobOfDriveItem(
              project.groupId,
              driveItem as DriveItem
            );
            const file = new File([blob], driveItem.name, {
              type: (driveItem as DriveItem).file.mimeType,
            });
            filesToDrop.push(file);
          }
        }
        filesToDrop.forEach(handleFileUpload);
        break;
      }
      case DND_TYPE_EMAIL_ATTACHMENT: {
        const blobUrl = await apiDownloadAttachmentToBase64(
          projectId,
          item.draggableMessageAttachment.messageId,
          item.draggableMessageAttachment.attachment
        );

        const res = await fetch(blobUrl);
        const blob = await res.blob();

        const file = new File(
          [blob],
          item.draggableMessageAttachment.attachment.name,
          {
            type: item.draggableMessageAttachment.attachment.contentType,
          }
        );

        handleFileUpload(file);
        break;
      }
    }
    setDropLoading(false);
  };

  return (
    <>
      <DroppableElement
        accept={[DND_TYPE_DRIVE_ITEM_FILE, DND_TYPE_EMAIL_ATTACHMENT]}
        onDrop={handleDndDrop}
        disable={disableDrag}
        object={{ projectId }}
        activeOverlay
      >
        <div
          className={classNames(
            classes.dropZone,
            size === 'small' && classes.dropZoneSmall,
            isDragOver && classes.dropZoneActive
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleUploadClick}
        >
          {dropLoading ? (
            <PrioSpinner size="default" />
          ) : (
            <Flex.Column alignItems="center" justifyContent="center">
              <FontAwesomeIcon
                icon={['fal', 'upload']}
                className={classes.uploadIcon}
              />
              <Typography.Text className={classes.dropZoneText}>
                {title || t('accounting:incomingInvoices.dropZoneDropText')}
              </Typography.Text>
              <Typography.Text className={classes.dropZoneSubtext}>
                <span
                  style={{
                    color: theme.colors.base.blue[80],
                    cursor: 'pointer',
                  }}
                  onClick={handleUploadClick}
                >
                  {subtitle ||
                    t('accounting:incomingInvoices.dropZoneClickText')}
                </span>
              </Typography.Text>
            </Flex.Column>
          )}
        </div>
      </DroppableElement>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.jpg,.jpeg,.png,.tiff"
        onChange={handleFileInputChange}
        disabled={isUploading}
      />
    </>
  );
};

export default FileDropZone;
